import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { GraduationCap, Search, Users } from 'lucide-react';
import { SlideIn, FadeIn, StaggerContainer, StaggerItem } from '@/components/motion';
import { CertificationInterestToggle } from '@/components/certification/CertificationInterestToggle';
import { InterestedFreelancerCard } from '@/components/certification/InterestedFreelancerCard';
import { CohortCard } from '@/components/certification/CohortCard';
import { CohortDetail } from '@/components/certification/CohortDetail';
import { CreateCohortDialog } from '@/components/certification/CreateCohortDialog';
import { useInterestedFreelancers } from '@/lib/hooks/use-interested-freelancers';
import { useCertificationCohorts } from '@/lib/hooks/use-certification-cohorts';

const TIMEZONE_OPTIONS = [
  'US/Eastern', 'US/Central', 'US/Mountain', 'US/Pacific',
  'Europe/London', 'Europe/Berlin', 'Asia/Dubai', 'Asia/Singapore',
  'Asia/Tokyo', 'Australia/Sydney',
];

function LoadingSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[1, 2, 3].map(i => (
        <Skeleton key={i} className="h-48 rounded-lg" />
      ))}
    </div>
  );
}

export default function CertificationHub() {
  const [activeTab, setActiveTab] = useState('partners');
  const [locationFilter, setLocationFilter] = useState('');
  const [timezoneFilter, setTimezoneFilter] = useState('');
  const [selectedCohortId, setSelectedCohortId] = useState<string | null>(null);

  const filters = {
    ...(locationFilter && { location: locationFilter }),
    ...(timezoneFilter && timezoneFilter !== 'all' && { timezone: timezoneFilter }),
  };

  // Only fetch when respective tab is active
  const { freelancers, isLoading: isLoadingFreelancers } = useInterestedFreelancers(
    activeTab === 'partners' ? filters : undefined
  );
  const { cohorts, myCohorts, isLoading: isLoadingCohorts, joinCohort, leaveCohort, isJoining, isLeaving } = useCertificationCohorts(
    activeTab === 'cohorts' ? undefined : '__disabled__'
  );

  const myMemberCohortIds = new Set(
    myCohorts?.map((m: any) => m.certification_cohorts?.id || m.cohort_id).filter(Boolean) || []
  );
  const myOrganizerCohortIds = new Set(
    myCohorts?.filter((m: any) => m.role === 'organizer').map((m: any) => m.certification_cohorts?.id || m.cohort_id).filter(Boolean) || []
  );

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <SlideIn direction="down">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <GraduationCap className="h-7 w-7 text-color-4" />
            Certification Hub
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Find partners and form cohorts for the Claude Certified Architect program (teams of 10)
          </p>
        </div>
      </SlideIn>

      <CertificationInterestToggle />

      {myCohorts && myCohorts.length > 0 && (
        <FadeIn>
          <div className="space-y-3">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Users className="h-4 w-4" />
              My Cohorts
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myCohorts.map((membership: any) => {
                const cohort = membership.certification_cohorts;
                if (!cohort) return null;
                return (
                  <CohortCard
                    key={cohort.id}
                    cohort={cohort}
                    isMember
                    isOrganizer={membership.role === 'organizer'}
                    onLeave={() => leaveCohort(cohort.id)}
                    onViewDetail={() => setSelectedCohortId(cohort.id)}
                    isLeaving={isLeaving}
                  />
                );
              })}
            </div>
          </div>
        </FadeIn>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="partners" className="flex items-center gap-1.5">
            <Search className="h-3.5 w-3.5" />
            Find Partners
          </TabsTrigger>
          <TabsTrigger value="cohorts" className="flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" />
            Cohorts
            {cohorts.length > 0 && (
              <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0">{cohorts.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="partners" className="mt-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <Input
                placeholder="Filter by location..."
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="h-9"
              />
            </div>
            <Select value={timezoneFilter} onValueChange={setTimezoneFilter}>
              <SelectTrigger className="w-full sm:w-48 h-9">
                <SelectValue placeholder="Timezone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Timezones</SelectItem>
                {TIMEZONE_OPTIONS.map(tz => (
                  <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isLoadingFreelancers ? (
            <LoadingSkeleton />
          ) : freelancers.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <GraduationCap className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                <p className="text-sm text-muted-foreground">
                  No Geniuses have opted in yet. Be the first — toggle the switch above!
                </p>
              </CardContent>
            </Card>
          ) : (
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {freelancers.map((f) => (
                <StaggerItem key={f.id}>
                  <InterestedFreelancerCard freelancer={f} />
                </StaggerItem>
              ))}
            </StaggerContainer>
          )}
        </TabsContent>

        <TabsContent value="cohorts" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {cohorts.length} cohort{cohorts.length !== 1 ? 's' : ''} available
            </p>
            <CreateCohortDialog />
          </div>

          {isLoadingCohorts ? (
            <LoadingSkeleton />
          ) : cohorts.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                <p className="text-sm text-muted-foreground">
                  No cohorts yet. Create the first one!
                </p>
              </CardContent>
            </Card>
          ) : (
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {cohorts.map((cohort) => (
                <StaggerItem key={cohort.id}>
                  <CohortCard
                    cohort={cohort}
                    isMember={myMemberCohortIds.has(cohort.id)}
                    isOrganizer={myOrganizerCohortIds.has(cohort.id)}
                    onJoin={() => joinCohort(cohort.id)}
                    onLeave={() => leaveCohort(cohort.id)}
                    onViewDetail={() => setSelectedCohortId(cohort.id)}
                    isJoining={isJoining}
                    isLeaving={isLeaving}
                  />
                </StaggerItem>
              ))}
            </StaggerContainer>
          )}
        </TabsContent>
      </Tabs>

      {selectedCohortId && (
        <CohortDetail
          cohortId={selectedCohortId}
          open={!!selectedCohortId}
          onOpenChange={(open) => !open && setSelectedCohortId(null)}
        />
      )}
    </div>
  );
}
